import { I18n } from "@/i18n/I18n";
import { useGlobalStore } from "../store/GlobalStore";
import { checkForToast } from "../toasts/checkForToast";
import { createFusaErrorFromResponse } from "@/errors/fusaError";
import { FusaReply } from "@/types/replyTypes";

export class Client {
  protected getHeaders() {
    const {accessToken} = useGlobalStore.getState();

    if (!accessToken) {
      throw new Error(I18n.get('userNotLoggedIn'));
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    };
  }

  // An expired session makes the backend reject the bearer token with a 401
  // and an empty body; surfacing that as a regular error leaves the user on a
  // broken page with an empty toast, so send them back to the login page.
  private handleExpiredSession() {
    const { redirectToLogin } = useGlobalStore.getState();
    redirectToLogin?.();
    const errorResponse = {
      status: 401,
      errors: [{
        message: I18n.get('sessionExpired'),
        hideToast: Boolean(redirectToLogin),
      }],
    };
    checkForToast(errorResponse);
    return errorResponse;
  }

  /** `quiet`: no toast for what comes back, the caller says how it went itself (each error is marked `hideToast`) */
  private async fetchAndCheckErrors(url: string, options: RequestInit, quiet = false) {
    const toast = (reply: FusaReply) => checkForToast(quiet ? { ...reply, toast: undefined, errors: reply.errors?.map((e) => ({ ...e, hideToast: true })) } : reply);
    try {
      const response = await fetch(url, options);
      if (response.status === 401) {
        return this.handleExpiredSession();
      }
      if (!response.ok) {
        const errorResponse = await createFusaErrorFromResponse(response);
        toast(errorResponse);
        return errorResponse;
      }
      const jsonResponse = await response.json();
      toast(jsonResponse);
      return jsonResponse;
    } catch (error) {
      if (!navigator.onLine) {
        const errorResponse = {
          status: 503,
          errors: [{ message: I18n.get('noInternetConnection') }]
        };
        toast(errorResponse);
        return errorResponse;
      }
      const errorResponse = {
        status: 500,
        errors: [{ message: I18n.get('unknownError') }]
      };
      toast(errorResponse);
      return errorResponse;
    }
  }

  async read(url: string) {
    const headers = this.getHeaders();
    return await this.fetchAndCheckErrors(url, { headers });
  }

  async update(url: string, request: any) {
    return await this.fetchAndCheckErrors(url, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(request),
    });
  }

  /** `quiet`: no toast for what comes back; `signal` aborts it, when it answers as a request that failed */
  async patch(url: string, request: any, options: { quiet?: boolean; signal?: AbortSignal } = {}) {
    return await this.fetchAndCheckErrors(url, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(request),
      signal: options.signal,
    }, options.quiet);
  }

  async create(url: string, request: any) {
    return await this.fetchAndCheckErrors(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(request),
    });
  }

  async delete(url: string) {
    return await this.fetchAndCheckErrors(url, {
      method: 'DELETE',
      headers: this.getHeaders()
    });
  }

  /**
   * POST, then read the response as server-sent events, handing each `data:`
   * payload over as it arrives. An error response comes back as the reply it
   * is — no toast, the caller shows it where the stream would have gone.
   */
  async postEventStream(url: string, request: any, onData: (data: string) => void, signal?: AbortSignal): Promise<FusaReply | null> {
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...this.getHeaders(), Accept: 'text/event-stream' },
      body: JSON.stringify(request),
      signal,
    });
    if (response.status === 401) {
      const { redirectToLogin } = useGlobalStore.getState();
      redirectToLogin?.();
      return { status: 401, errors: [{ message: I18n.get('sessionExpired') }] } as FusaReply;
    }
    if (!response.ok || !response.body) return await createFusaErrorFromResponse(response);
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let end = buffer.indexOf('\n\n');
      while (end >= 0) {
        for (const line of buffer.slice(0, end).split('\n')) {
          if (line.startsWith('data: ')) onData(line.slice(6));
        }
        buffer = buffer.slice(end + 2);
        end = buffer.indexOf('\n\n');
      }
    }
    return null;
  }
}